import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Loading, EmptyState } from '../components/ui';
import * as XLSX from 'xlsx';

const API = '/api';

function authHeaders() {
  const token = localStorage.getItem('lc_token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' };
}

// ─── Skill cell colour ────────────────────────────────────────────────────────
function skillStyle(pct) {
  if (pct === null || pct === undefined) return { background: 'transparent', color: 'var(--text-secondary)' };
  if (pct >= 80) return { background: '#EAF3DE', color: '#3B6D11' };
  if (pct >= 60) return { background: '#FAEEDA', color: '#854F0B' };
  return { background: '#FCEBEB', color: '#A32D2D' };
}

function avgSkillStyle(pct) {
  if (!pct) return {};
  if (pct >= 80) return { color: '#3B6D11', fontWeight: 700 };
  if (pct >= 60) return { color: '#854F0B', fontWeight: 700 };
  return { color: '#A32D2D', fontWeight: 700 };
}

// ─── Export to Excel ──────────────────────────────────────────────────────────
function exportToExcel(operators, operations, cells, summary) {
  const colLabel = op => op.machine_type ? `${op.name} (${op.machine_type})` : op.name;
  const header = ['Operator', 'Emp ID', 'Line', ...operations.map(colLabel), 'Avg Skill%', 'Ops Known'];
  const rows = operators.map(emp => {
    const empCells = cells[emp.id] || {};
    const opValues = operations.map(op => {
      const cell = empCells[op.col_key];
      return cell ? `${cell.skill_pct}% (${cell.capacity_per_hr ?? '—'}/hr)` : '—';
    });
    return [emp.name, emp.emp_code, emp.line_number, ...opValues, emp.avg_skill ? `${emp.avg_skill}%` : '—', emp.ops_known];
  });

  // Summary row
  const summaryRow = ['% Qualified', '', '', ...operations.map(op => {
    const s = summary[op.col_key];
    return s ? `${s.pct}% (${s.qualified_count}/${s.total})` : '—';
  }), '', ''];

  const wsData = [header, ...rows, summaryRow];
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Column widths
  ws['!cols'] = [{ wch: 20 }, { wch: 12 }, { wch: 8 },
    ...operations.map(() => ({ wch: 16 })), { wch: 10 }, { wch: 10 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Skill Matrix');
  XLSX.writeFile(wb, `SkillMatrix_${new Date().toISOString().split('T')[0]}.xlsx`);
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function SkillMatrix() {
  const [filters, setFilters] = useState({ factories: [], lines: [] });
  const [selectedDivision, setSelectedDivision] = useState('');
  const [selectedFactory, setSelectedFactory] = useState('');
  const [selectedLine, setSelectedLine] = useState('');
  const [selectedShift, setSelectedShift] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const tableRef = useRef(null);

  useEffect(() => {
    const el = document.getElementById('lc-page-title');
    if (el) el.textContent = 'Skill matrix';
  }, []);

  // Load filter options
  useEffect(() => {
    fetch(`${API}/skill-matrix/filters`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setFilters(d))
      .catch(() => {});
  }, []);

  const loadMatrix = useCallback(() => {
    setLoading(true);
    setError('');
    const params = new URLSearchParams();
    if (selectedLine) params.append('shift_line_id', selectedLine);
    else if (selectedFactory) params.append('factory_id', selectedFactory);
    else if (selectedDivision) params.append('division_id', selectedDivision);
    if (selectedShift) params.append('shift_name', selectedShift);

    fetch(`${API}/skill-matrix?${params}`, { headers: authHeaders() })
      .then(async r => {
        const d = await r.json();
        if (!r.ok) { setError(d.error || 'Error loading matrix'); setLoading(false); return; }
        setData(d);
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [selectedDivision, selectedFactory, selectedLine, selectedShift]);

  // Auto-load when filters change
  useEffect(() => {
    loadMatrix();
  }, [loadMatrix]);

  const filteredFactories = selectedDivision
    ? (filters.factories || []).filter(f => String(f.division_id) === String(selectedDivision))
    : (filters.factories || []);
  const filteredLines = (filters.lines || []).filter(l =>
    (!selectedFactory || String(l.factory_id) === String(selectedFactory)) &&
    (!selectedDivision || filteredFactories.some(f => String(f.id) === String(l.factory_id)))
  );
  const shiftOptions = [...new Set((filters.lines || []).map(l => l.shift_name).filter(Boolean))].sort();

  const { operators = [], operations = [], cells = {}, summary = {}, ops_needing_training = [], operators_needing_training = [] } = data || {};

  const thStyle = {
    padding: '6px 8px', fontSize: 10, fontWeight: 700,
    color: 'var(--text-secondary)', textTransform: 'uppercase',
    background: 'var(--bg-secondary)', borderBottom: '0.5px solid var(--border)',
    borderRight: '0.5px solid var(--border)', whiteSpace: 'nowrap',
    position: 'sticky', top: 0, zIndex: 2
  };

  const thRotateStyle = {
    ...thStyle,
    writingMode: 'vertical-rl',
    transform: 'rotate(180deg)',
    height: 100, verticalAlign: 'bottom',
    padding: '8px 4px',
  };

  const tdStyle = {
    padding: '5px 8px', fontSize: 12, borderBottom: '0.5px solid var(--border)',
    borderRight: '0.5px solid var(--border)', textAlign: 'center', whiteSpace: 'nowrap'
  };

  const stickyColStyle = {
    position: 'sticky', left: 0, zIndex: 1,
    background: '#fff', borderRight: '1px solid var(--border)'
  };

  return (
    <div style={{ padding: 24, background: 'var(--bg-tertiary)', minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Skill Matrix</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
            Operator skills across all operations
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {/* Division filter */}
          <select className="inp" value={selectedDivision} onChange={e => { setSelectedDivision(e.target.value); setSelectedFactory(''); setSelectedLine(''); }} style={{ width: 140 }}>
            <option value="">All divisions</option>
            {(filters.divisions || []).map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>

          {/* Factory filter */}
          <select className="inp" value={selectedFactory} onChange={e => { setSelectedFactory(e.target.value); setSelectedLine(''); }} style={{ width: 160 }}>
            <option value="">All factories</option>
            {filteredFactories.map(f => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>

          {/* Line filter */}
          <select className="inp" value={selectedLine} onChange={e => setSelectedLine(e.target.value)} style={{ width: 140 }}>
            <option value="">All lines</option>
            {filteredLines.map(l => (
              <option key={l.id} value={l.id}>Line {l.line_number}</option>
            ))}
          </select>

          {/* Shift filter */}
          <select className="inp" value={selectedShift} onChange={e => setSelectedShift(e.target.value)} style={{ width: 130 }}>
            <option value="">All shifts</option>
            {shiftOptions.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          {/* Export */}
          {data && operators.length > 0 && (
            <button onClick={() => exportToExcel(operators, operations, cells, summary)}
              className="btn btn-s" style={{ whiteSpace: 'nowrap' }}>
              ↓ Export Excel
            </button>
          )}
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Skill legend:</span>
        {[
          { label: '≥ 80% Skilled', bg: '#EAF3DE', color: '#3B6D11' },
          { label: '60–79% Developing', bg: '#FAEEDA', color: '#854F0B' },
          { label: '< 60% Training needed', bg: '#FCEBEB', color: '#A32D2D' },
          { label: '— Not trained', bg: 'transparent', color: 'var(--text-secondary)' },
        ].map(l => (
          <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 14, height: 14, borderRadius: 3, background: l.bg, border: '0.5px solid rgba(0,0,0,0.1)' }} />
            <span style={{ fontSize: 11, color: l.color }}>{l.label}</span>
          </div>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: 'var(--red-bg)', border: '1px solid var(--red)', borderRadius: 7, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--red)' }}>
          {error}
        </div>
      )}

      {loading && <Loading />}

      {!loading && operators.length === 0 && (
        <EmptyState message="No operators found for the selected filters." />
      )}

      {/* Matrix table */}
      {!loading && operators.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 8, border: '0.5px solid var(--border)', overflow: 'auto', maxHeight: 'calc(100vh - 260px)' }} ref={tableRef}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: '100%' }}>
            <thead>
              <tr>
                {/* Fixed headers */}
                <th style={{ ...thStyle, ...stickyColStyle, left: 0, zIndex: 3, minWidth: 160, textAlign: 'left' }}>Operator</th>
                <th style={{ ...thStyle, position: 'sticky', left: 160, zIndex: 3, minWidth: 80 }}>Emp ID</th>
                <th style={{ ...thStyle, position: 'sticky', left: 240, zIndex: 3, minWidth: 60 }}>Line</th>

                {/* Operation headers — rotated, now one per (operation, machine) */}
                {operations.map(op => (
                  <th key={op.col_key} style={thRotateStyle} title={op.machine_type ? `${op.name} (${op.machine_type})` : op.name}>
                    {op.name}{op.machine_type ? ` · ${op.machine_type}` : ''}
                  </th>
                ))}

                {/* Summary headers */}
                <th style={{ ...thStyle, background: 'var(--acc-bg)', color: 'var(--acc)', minWidth: 80 }}>Avg Skill%</th>
                <th style={{ ...thStyle, background: 'var(--acc-bg)', color: 'var(--acc)', minWidth: 80 }}>Ops Known</th>
              </tr>
            </thead>

            <tbody>
              {operators.map((emp, rowIdx) => {
                const empCells = cells[emp.id] || {};
                return (
                  <tr key={emp.id} style={{ background: rowIdx % 2 === 0 ? '#fff' : '#fafafa' }}>
                    {/* Operator name */}
                    <td style={{ ...tdStyle, ...stickyColStyle, textAlign: 'left', fontWeight: 600, background: rowIdx % 2 === 0 ? '#fff' : '#fafafa' }}>
                      {emp.name}
                      {emp.ops_known < 2 && <span style={{ marginLeft: 6, fontSize: 9, background: '#FCEBEB', color: '#A32D2D', borderRadius: 3, padding: '1px 4px', fontWeight: 700 }}>Training needed</span>}
                    </td>
                    {/* Emp ID */}
                    <td style={{ ...tdStyle, position: 'sticky', left: 160, zIndex: 1, background: rowIdx % 2 === 0 ? '#fff' : '#fafafa', color: 'var(--text-secondary)', fontSize: 11 }}>
                      {emp.emp_code}
                    </td>
                    {/* Line */}
                    <td style={{ ...tdStyle, position: 'sticky', left: 240, zIndex: 1, background: rowIdx % 2 === 0 ? '#fff' : '#fafafa' }}>
                      {emp.line_number}
                    </td>

                    {/* Skill cells — now one per (operation, machine); shows skill% + capacity/hr */}
                    {operations.map(op => {
                      const cell = empCells[op.col_key];
                      return (
                        <td key={op.col_key} style={{ ...tdStyle, ...skillStyle(cell?.skill_pct) }}>
                          {cell ? (
                            <>
                              <div>{cell.skill_pct}%</div>
                              {cell.capacity_per_hr != null && (
                                <div style={{ fontSize: 9, opacity: 0.75, fontWeight: 400 }}>{cell.capacity_per_hr}/hr</div>
                              )}
                            </>
                          ) : '—'}
                        </td>
                      );
                    })}

                    {/* Avg skill summary */}
                    <td style={{ ...tdStyle, background: 'var(--acc-bg)', ...avgSkillStyle(emp.avg_skill) }}>
                      {emp.avg_skill ? `${emp.avg_skill}%` : '—'}
                    </td>

                    {/* Ops known */}
                    <td style={{ ...tdStyle, background: 'var(--acc-bg)', fontWeight: 600 }}>
                      {emp.ops_known}
                    </td>
                  </tr>
                );
              })}

              {/* Summary row — % qualified per operation */}
              <tr style={{ background: 'var(--bg-secondary)', borderTop: '2px solid var(--border)' }}>
                <td style={{ ...tdStyle, ...stickyColStyle, textAlign: 'left', fontWeight: 700, fontSize: 11, color: 'var(--text-secondary)', background: 'var(--bg-secondary)' }}>
                  % Operators qualified
                </td>
                <td style={{ ...tdStyle, position: 'sticky', left: 160, zIndex: 1, background: 'var(--bg-secondary)' }} />
                <td style={{ ...tdStyle, position: 'sticky', left: 240, zIndex: 1, background: 'var(--bg-secondary)' }} />
                {operations.map(op => {
                  const s = summary[op.col_key];
                  return (
                    <td key={op.col_key} style={{ ...tdStyle, ...skillStyle(s?.pct), fontWeight: 700, fontSize: 11 }}>
                      {s ? `${s.pct}%` : '—'}
                    </td>
                  );
                })}
                <td style={{ ...tdStyle, background: 'var(--bg-secondary)' }} />
                <td style={{ ...tdStyle, background: 'var(--bg-secondary)' }} />
              </tr>

              {/* Operators per operation row */}
              <tr style={{ background: '#FFF8E1', borderTop: '1px solid var(--border)' }}>
                <td style={{ ...tdStyle, ...stickyColStyle, textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#854F0B', background: '#FFF8E1' }}>
                  Operators / operation
                </td>
                <td style={{ ...tdStyle, position: 'sticky', left: 160, zIndex: 1, background: '#FFF8E1' }} />
                <td style={{ ...tdStyle, position: 'sticky', left: 240, zIndex: 1, background: '#FFF8E1' }} />
                {operations.map(op => {
                  const s = summary[op.col_key];
                  const count = s?.operators_count ?? 0;
                  const needsTraining = count < 2;
                  return (
                    <td key={op.col_key} style={{
                      ...tdStyle,
                      background: needsTraining ? '#FCEBEB' : count >= 3 ? '#EAF3DE' : '#FFF8E1',
                      color: needsTraining ? '#A32D2D' : count >= 3 ? '#3B6D11' : '#854F0B',
                      fontWeight: 700, fontSize: 12
                    }}>
                      {count}
                      {needsTraining && <span style={{ display: 'block', fontSize: 8, fontWeight: 600 }}>⚠ Low</span>}
                    </td>
                  );
                })}
                <td style={{ ...tdStyle, background: '#FFF8E1' }} />
                <td style={{ ...tdStyle, background: '#FFF8E1', fontWeight: 700, color: '#854F0B', fontSize: 11 }}>
                  {operators_needing_training.length > 0 ? `${operators_needing_training.length} ops<2` : '✓'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Training need summary */}
      {(ops_needing_training.length > 0 || operators_needing_training.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 16 }}>
          {ops_needing_training.length > 0 && (
            <div style={{ background: '#FCEBEB', border: '1px solid #A32D2D', borderRadius: 8, padding: '12px 16px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#A32D2D', marginBottom: 8 }}>
                ⚠ Operations with &lt; 2 trained operators ({ops_needing_training.length})
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {ops_needing_training.map(name => (
                  <span key={name} style={{ fontSize: 11, background: '#fff', border: '0.5px solid #A32D2D', color: '#A32D2D', borderRadius: 4, padding: '2px 8px' }}>{name}</span>
                ))}
              </div>
            </div>
          )}
          {operators_needing_training.length > 0 && (
            <div style={{ background: '#FAEEDA', border: '1px solid #854F0B', borderRadius: 8, padding: '12px 16px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#854F0B', marginBottom: 8 }}>
                ⚠ Operators who know &lt; 2 operations ({operators_needing_training.length})
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {operators_needing_training.map(emp => (
                  <span key={emp.emp_code} style={{ fontSize: 11, background: '#fff', border: '0.5px solid #854F0B', color: '#854F0B', borderRadius: 4, padding: '2px 8px' }}>
                    {emp.name} ({emp.ops_known})
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}